// File System Access mount engine for the web build.
//
// Holds the registry of directory mounts (virtual absolute paths like
// /fsa/<slug> backed by FileSystemDirectoryHandle) plus a session-only
// write overlay, and implements the async fs operations against them.
// The fs shims (fs-promises.cjs et al) consult this module first and fall
// back to their baseline no-filesystem behavior when an operation returns
// the NOT_HANDLED sentinel.
//
// Plain CommonJS so both the .cjs shims and the TS web modules share the
// single webpack-deduped instance.
const { Buffer } = require("buffer");

const opfs = require("./opfs-store.cjs");

const NOT_HANDLED = Symbol("fsa-not-handled");

/** @type {Map<string, {state: "live"|"locked", handle: any, label: string, rootStat: {mtimeMs: number}}>} */
const mounts = new Map();

/** @type {Map<string, {kind: "file"|"dir", data: Uint8Array|null, mtimeMs: number}>} */
const overlay = new Map();

/**
 * Single picked files (the "Install From File" archive) mounted by virtual
 * path. Backed by a File/FileSystemFileHandle, read-only.
 * @type {Map<string, {file: File|null, handle: any, label: string}>}
 */
const fileMounts = new Map();

// Subtrees of the virtual userData that are routed to OPFS — persistent and
// streamable — instead of the in-memory overlay, so they survive reloads:
//  - the mod staging tree (`{USERDATA}/<game>/mods`, plus the
//    `<modId>.installing` temp dirs beside it)
//  - the game-image cache (`{USERDATA}/icon_cache`), so the sidebar game
//    icons resolve after a reload (the cache stores the resolved thumbnail
//    URL; see prewarmSync + readFileSync for the sync-load path)
// We scope to these specifically rather than all of userData: routing the
// downloads dir to OPFS makes it "exist" and trips a folder-validity check.
const STAGING_RE = /^\/vortex\/userData\/[^/]+\/mods(\/|$)/;
const ICON_CACHE = "/vortex/userData/icon_cache";

function isPersistent(norm) {
    return (
        STAGING_RE.test(norm) ||
        norm === ICON_CACHE ||
        norm.startsWith(ICON_CACHE + "/")
    );
}

// Snapshot of selected small OPFS files, preloaded at boot so the renderer's
// synchronous reads (readFileSync — which the browser fs otherwise can't
// serve) succeed. Currently used for the game-image cache JSON.
const syncFiles = new Map();

function readSyncSnapshot(p) {
    return syncFiles.get(normalize(p));
}

// Recursively load every file under an OPFS subtree into the sync snapshot.
// Called from boot before the renderer evaluates.
async function prewarmSync(prefix) {
    if (!(await opfs.available())) {
        return;
    }
    const walk = async (dirNorm) => {
        let entries;
        try {
            entries = await opfs.readdir(dirNorm, true);
        } catch (err) {
            return;
        }
        for (const entry of entries) {
            const childNorm = normalize(dirNorm + "/" + entry.name);
            if (entry.isDirectory()) {
                await walk(childNorm);
            } else {
                try {
                    syncFiles.set(childNorm, await opfs.readFile(childNorm));
                } catch (err) {
                    /* skip unreadable entries */
                }
            }
        }
    };
    await walk(normalize(prefix));
}

// ---------------------------------------------------------------------------
// paths & errors
// ---------------------------------------------------------------------------

function normalize(p) {
    let norm = String(p).replace(/\\/g, "/").replace(/\/+/g, "/");
    if (norm.length > 1 && norm.endsWith("/")) {
        norm = norm.slice(0, -1);
    }
    return norm;
}

function nodeError(code, errno, filePath, message) {
    const err = new Error(`${code}: ${message}, '${filePath}'`);
    err.code = code;
    err.errno = errno;
    err.path = filePath;
    return err;
}

const enoent = (p) => nodeError("ENOENT", -2, p, "no such file or directory");
const eperm = (p) => nodeError("EPERM", -1, p, "operation not permitted");

function mapDomError(err, filePath) {
    const name = err?.name;
    if (name === "NotFoundError" || name === "TypeMismatchError") {
        return enoent(filePath);
    }
    if (name === "NotAllowedError" || name === "SecurityError") {
        return eperm(filePath);
    }
    return err;
}

// stable pseudo-inode so getNormalizeFunc's ino comparisons behave sanely
function pseudoIno(normPath) {
    let hash = 0;
    for (let i = 0; i < normPath.length; i++) {
        hash = (hash * 31 + normPath.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
}

function makeStats(normPath, { isDir, size = 0, mtimeMs = 0 }) {
    const mtime = new Date(mtimeMs);
    return {
        isFile: () => !isDir,
        isDirectory: () => isDir,
        isSymbolicLink: () => false,
        isBlockDevice: () => false,
        isCharacterDevice: () => false,
        isFIFO: () => false,
        isSocket: () => false,
        size,
        mode: isDir ? 0o755 : 0o644,
        mtime,
        atime: mtime,
        ctime: mtime,
        birthtime: mtime,
        mtimeMs,
        ino: pseudoIno(normPath),
        dev: 0,
        nlink: 1,
        uid: 0,
        gid: 0,
    };
}

// ---------------------------------------------------------------------------
// mount registry
// ---------------------------------------------------------------------------

function registerMount(mountPath, handle, meta = {}) {
    const norm = normalize(mountPath);
    mounts.set(norm, {
        state: "live",
        handle,
        label: meta.label ?? handle?.name ?? norm,
        rootStat: meta.rootStat ?? { mtimeMs: 0 },
    });
    return norm;
}

function registerLockedMount(mountPath, meta = {}) {
    const norm = normalize(mountPath);
    mounts.set(norm, {
        state: "locked",
        handle: meta.handle ?? null,
        label: meta.label ?? norm,
        rootStat: meta.rootStat ?? { mtimeMs: 0 },
    });
    return norm;
}

function unlockMount(mountPath, handle) {
    const norm = normalize(mountPath);
    const entry = mounts.get(norm);
    if (entry !== undefined) {
        entry.state = "live";
        if (handle !== undefined) {
            entry.handle = handle;
        }
    }
}

function listMounts() {
    return Array.from(mounts.entries()).map(([mountPath, entry]) => ({
        mountPath,
        state: entry.state,
        label: entry.label,
        handle: entry.handle,
    }));
}

/**
 * Register a single picked file at a virtual path so the renderer can read it
 * by path (e.g. the "Install From File" archive). `source` is a File or a
 * FileSystemFileHandle.
 */
function registerFileMount(filePath, source, label) {
    const norm = normalize(filePath);
    const isHandle =
        source !== null &&
        typeof source === "object" &&
        typeof source.getFile === "function";
    fileMounts.set(norm, {
        file: isHandle ? null : source,
        handle: isHandle ? source : null,
        label: label ?? norm,
    });
    return norm;
}

async function fileMountGet(norm) {
    const entry = fileMounts.get(norm);
    if (entry === undefined) {
        return null;
    }
    if (entry.file !== null) {
        return entry.file;
    }
    return entry.handle.getFile();
}

/**
 * Longest-prefix mount match. Returns {mountPath, entry, rest} or null.
 * rest is the path below the mount root as an array of segments.
 */
function findMount(p) {
    const norm = normalize(p);
    let best = null;
    for (const [mountPath, entry] of mounts) {
        if (norm === mountPath || norm.startsWith(mountPath + "/")) {
            if (best === null || mountPath.length > best.mountPath.length) {
                best = { mountPath, entry };
            }
        }
    }
    if (best === null) {
        return null;
    }
    const restStr = norm.slice(best.mountPath.length).replace(/^\//, "");
    return {
        mountPath: best.mountPath,
        entry: best.entry,
        rest: restStr.length > 0 ? restStr.split("/") : [],
    };
}

// ---------------------------------------------------------------------------
// handle resolution
// ---------------------------------------------------------------------------

/**
 * Resolves a path below a live mount to a handle.
 * Returns {kind: "file"|"dir", handle}. Throws node-style errors.
 */
async function resolveHandle(mount, rest, filePath) {
    let dir = mount.entry.handle;
    try {
        for (let i = 0; i < rest.length - 1; i++) {
            dir = await dir.getDirectoryHandle(rest[i]);
        }
        if (rest.length === 0) {
            return { kind: "dir", handle: dir };
        }
        const leaf = rest[rest.length - 1];
        try {
            return { kind: "file", handle: await dir.getFileHandle(leaf) };
        } catch (err) {
            if (
                err?.name === "TypeMismatchError" ||
                err?.name === "NotFoundError"
            ) {
                return {
                    kind: "dir",
                    handle: await dir.getDirectoryHandle(leaf),
                };
            }
            throw err;
        }
    } catch (err) {
        throw mapDomError(err, filePath);
    }
}

// ---------------------------------------------------------------------------
// write-through to a live (read-write) FSA mount — used for mod deployment,
// which copies staged files into the real game folder
// ---------------------------------------------------------------------------

// resolves the directory handle containing the final path segment, creating
// intermediate dirs. Returns { dir, name } or throws a node-style error.
async function resolveWritableParent(mount, rest, filePath) {
    let dir = mount.entry.handle;
    try {
        for (let i = 0; i < rest.length - 1; i++) {
            dir = await dir.getDirectoryHandle(rest[i], { create: true });
        }
    } catch (err) {
        throw mapDomError(err, filePath);
    }
    return { dir, name: rest[rest.length - 1] };
}

async function mountWriteFile(mount, rest, filePath, data) {
    if (rest.length === 0) {
        throw eperm(filePath);
    }
    const { dir, name } = await resolveWritableParent(mount, rest, filePath);
    try {
        const fileHandle = await dir.getFileHandle(name, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(toData(data));
        await writable.close();
    } catch (err) {
        throw mapDomError(err, filePath);
    }
}

async function mountMkdir(mount, rest) {
    let dir = mount.entry.handle;
    for (const seg of rest) {
        dir = await dir.getDirectoryHandle(seg, { create: true });
    }
}

async function mountRemove(mount, rest, filePath, recursive) {
    if (rest.length === 0) {
        throw eperm(filePath);
    }
    const { dir, name } = await resolveWritableParent(mount, rest, filePath);
    try {
        await dir.removeEntry(name, { recursive });
    } catch (err) {
        if (err?.name === "NotFoundError") {
            return; // already gone
        }
        throw mapDomError(err, filePath);
    }
}

// ---------------------------------------------------------------------------
// overlay helpers
// ---------------------------------------------------------------------------

function overlayWrite(normPath, data) {
    // implicitly create parent dir entries so stat/readdir of parents works
    const segments = normPath.split("/").filter((seg) => seg.length > 0);
    let parent = "";
    for (let i = 0; i < segments.length - 1; i++) {
        parent += "/" + segments[i];
        if (!overlay.has(parent)) {
            overlay.set(parent, {
                kind: "dir",
                data: null,
                mtimeMs: Date.now(),
            });
        }
    }
    overlay.set(normPath, {
        kind: "file",
        data,
        mtimeMs: Date.now(),
    });
}

function overlayMkdir(normPath) {
    const segments = normPath.split("/").filter((seg) => seg.length > 0);
    let current = "";
    for (const seg of segments) {
        current += "/" + seg;
        if (!overlay.has(current)) {
            overlay.set(current, {
                kind: "dir",
                data: null,
                mtimeMs: Date.now(),
            });
        }
    }
}

function overlayChildren(normPath) {
    const prefix = normPath === "/" ? "/" : normPath + "/";
    const names = new Set();
    for (const key of overlay.keys()) {
        if (key.startsWith(prefix)) {
            const sub = key.slice(prefix.length);
            const name = sub.split("/")[0];
            if (name.length > 0) {
                names.add(name);
            }
        }
    }
    return names;
}

function toData(data, options) {
    if (typeof data === "string") {
        return new TextEncoder().encode(data);
    }
    if (data instanceof Uint8Array) {
        return data;
    }
    if (data?.buffer instanceof ArrayBuffer) {
        return new Uint8Array(
            data.buffer,
            data.byteOffset ?? 0,
            data.byteLength,
        );
    }
    return new TextEncoder().encode(String(data ?? ""));
}

function encodingOf(options) {
    if (typeof options === "string") {
        return options;
    }
    if (options !== null && typeof options === "object") {
        return options.encoding ?? null;
    }
    return null;
}

function decode(bytes, encoding) {
    if (encoding === null || encoding === undefined) {
        return Buffer.from(bytes);
    }
    return Buffer.from(bytes).toString(encoding);
}

function makeDirent(name, isDir) {
    return {
        name,
        isDirectory: () => isDir,
        isFile: () => !isDir,
        isSymbolicLink: () => false,
    };
}

// ---------------------------------------------------------------------------
// fs operations (return NOT_HANDLED when the path is outside our world)
// ---------------------------------------------------------------------------

async function stat(p) {
    const norm = normalize(p);

    if (fileMounts.has(norm)) {
        const file = await fileMountGet(norm);
        return makeStats(norm, {
            isDir: false,
            size: file.size,
            mtimeMs: file.lastModified ?? 0,
        });
    }

    if (isPersistent(norm) && (await opfs.available())) {
        const info = await opfs.stat(norm); // throws ENOENT if missing
        return makeStats(norm, info);
    }

    const shadow = overlay.get(norm);
    if (shadow !== undefined) {
        return makeStats(norm, {
            isDir: shadow.kind === "dir",
            size: shadow.data?.byteLength ?? 0,
            mtimeMs: shadow.mtimeMs,
        });
    }

    const mount = findMount(norm);
    if (mount === null) {
        return NOT_HANDLED;
    }

    if (mount.entry.state === "locked") {
        if (mount.rest.length === 0) {
            // serve the cached root stat so persisted discovery state survives a
            // reload while the permission re-grant is still pending
            return makeStats(norm, {
                isDir: true,
                mtimeMs: mount.entry.rootStat.mtimeMs,
            });
        }
        throw eperm(norm);
    }

    const resolved = await resolveHandle(mount, mount.rest, norm);
    if (resolved.kind === "file") {
        const file = await resolved.handle.getFile();
        return makeStats(norm, {
            isDir: false,
            size: file.size,
            mtimeMs: file.lastModified,
        });
    }
    return makeStats(norm, { isDir: true, mtimeMs: 0 });
}

async function readFile(p, options) {
    const norm = normalize(p);
    const encoding = encodingOf(options);

    if (fileMounts.has(norm)) {
        const file = await fileMountGet(norm);
        const bytes = new Uint8Array(await file.arrayBuffer());
        return decode(bytes, encoding);
    }

    if (isPersistent(norm) && (await opfs.available())) {
        const bytes = await opfs.readFile(norm); // throws ENOENT if missing
        return decode(bytes, encoding);
    }

    const shadow = overlay.get(norm);
    if (shadow !== undefined) {
        if (shadow.kind === "dir") {
            throw nodeError(
                "EISDIR",
                -21,
                norm,
                "illegal operation on a directory",
            );
        }
        return decode(shadow.data ?? new Uint8Array(), encoding);
    }

    const mount = findMount(norm);
    if (mount === null) {
        return NOT_HANDLED;
    }
    if (mount.entry.state === "locked") {
        throw eperm(norm);
    }

    const resolved = await resolveHandle(mount, mount.rest, norm);
    if (resolved.kind === "dir") {
        throw nodeError(
            "EISDIR",
            -21,
            norm,
            "illegal operation on a directory",
        );
    }
    let file;
    try {
        file = await resolved.handle.getFile();
    } catch (err) {
        throw mapDomError(err, norm);
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    return decode(bytes, encoding);
}

async function readdir(p, options) {
    const norm = normalize(p);
    const withFileTypes =
        options !== null &&
        typeof options === "object" &&
        options.withFileTypes === true;

    if (isPersistent(norm) && (await opfs.available())) {
        return opfs.readdir(norm, withFileTypes);
    }

    const mount = findMount(norm);
    const shadowNames = overlayChildren(norm);

    if (mount === null && shadowNames.size === 0) {
        const shadow = overlay.get(norm);
        if (shadow !== undefined && shadow.kind === "dir") {
            return []; // empty overlay dir
        }
        return NOT_HANDLED;
    }

    /** @type {Map<string, boolean>} name -> isDir */
    const entries = new Map();

    if (mount !== null && mount.entry.state === "live") {
        const resolved = await resolveHandle(mount, mount.rest, norm);
        if (resolved.kind !== "dir") {
            throw nodeError("ENOTDIR", -20, norm, "not a directory");
        }
        try {
            for await (const child of resolved.handle.values()) {
                entries.set(child.name, child.kind === "directory");
            }
        } catch (err) {
            throw mapDomError(err, norm);
        }
    }
    // locked mounts list as empty (plus any overlay entries)

    for (const name of shadowNames) {
        const childPath = norm + "/" + name;
        const child = overlay.get(childPath);
        entries.set(name, child !== undefined ? child.kind === "dir" : true);
    }

    const names = Array.from(entries.keys()).sort();
    if (withFileTypes) {
        return names.map((name) => makeDirent(name, entries.get(name)));
    }
    return names;
}

// returns the mount when the path is under a LIVE read-write mount (so writes
// go through to the real folder — mod deployment into the game directory)
function liveMount(norm) {
    const mount = findMount(norm);
    return mount !== null && mount.entry.state === "live" ? mount : null;
}

async function writeFile(p, data, _options) {
    const norm = normalize(p);
    // staging tree -> OPFS (persistent); live game mount -> write through to
    // the real folder (deployment); everything else -> session overlay.
    if (isPersistent(norm) && (await opfs.available())) {
        return opfs.writeFile(norm, toData(data));
    }
    const mount = liveMount(norm);
    if (mount !== null) {
        return mountWriteFile(mount, mount.rest, norm, data);
    }
    overlayWrite(norm, toData(data));
}

async function mkdir(p, _options) {
    const norm = normalize(p);
    if (isPersistent(norm) && (await opfs.available())) {
        return opfs.mkdir(norm);
    }
    const mount = liveMount(norm);
    if (mount !== null) {
        return mountMkdir(mount, mount.rest);
    }
    overlayMkdir(norm);
}

async function unlink(p) {
    const norm = normalize(p);
    if (isPersistent(norm) && (await opfs.available())) {
        return opfs.remove(norm);
    }
    const mount = liveMount(norm);
    if (mount !== null) {
        return mountRemove(mount, mount.rest, norm, false);
    }
    overlay.delete(norm);
}

async function rm(p, _options) {
    const norm = normalize(p);
    if (isPersistent(norm) && (await opfs.available())) {
        return opfs.remove(norm);
    }
    const mount = liveMount(norm);
    if (mount !== null) {
        return mountRemove(mount, mount.rest, norm, true);
    }
    const prefix = norm + "/";
    for (const key of Array.from(overlay.keys())) {
        if (key === norm || key.startsWith(prefix)) {
            overlay.delete(key);
        }
    }
}

async function rename(from, to) {
    const normFrom = normalize(from);
    const normTo = normalize(to);
    if (
        isPersistent(normFrom) &&
        isPersistent(normTo) &&
        (await opfs.available())
    ) {
        return opfs.rename(normFrom, normTo);
    }
    // cross-store / game-mount renames: copy then remove
    const fromMount = liveMount(normFrom);
    const toMountOrPersist =
        liveMount(normTo) !== null ||
        (isPersistent(normTo) && (await opfs.available()));
    if (fromMount !== null || toMountOrPersist) {
        const data = await readFile(normFrom);
        if (data !== NOT_HANDLED) {
            await writeFile(normTo, data);
            await rm(normFrom);
            return;
        }
    }
    const prefix = normFrom + "/";
    for (const [key, value] of Array.from(overlay.entries())) {
        if (key === normFrom || key.startsWith(prefix)) {
            overlay.delete(key);
            overlay.set(normTo + key.slice(normFrom.length), value);
        }
    }
}

async function exists(p) {
    const result = await stat(p).catch(() => null);
    if (result === NOT_HANDLED) {
        return NOT_HANDLED;
    }
    return result !== null;
}

async function access(p) {
    const result = await stat(p);
    if (result === NOT_HANDLED) {
        return NOT_HANDLED;
    }
    return undefined;
}

const core = {
    NOT_HANDLED,
    normalize,
    registerMount,
    registerLockedMount,
    unlockMount,
    listMounts,
    findMount,
    registerFileMount,
    readSyncSnapshot,
    prewarmSync,
    stat,
    readFile,
    readdir,
    writeFile,
    mkdir,
    unlink,
    rm,
    rename,
    exists,
    access,
};

// debugging / test access
globalThis.__vortexFsa = core;

module.exports = core;
